import { useMemo, useRef } from "react";
import cn from "classnames";
import { ChevronIcon } from "components/UI/Icons";
import { useClickOutside } from "hooks/useClickOutside";
import { SelectOption } from "../../../../types/select.types";
import { useOpenState, useSelection } from "../context/SelectContext";
import styles from "../Select.module.scss";

type SelectHeaderProps = {
  selectValue?: SelectOption[];
  renderIcon?: JSX.Element;
  headerClassName?: string;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  multiple?: boolean;
};

export const SelectHeader = ({
  selectValue,
  renderIcon,
  headerClassName,
  placeholder,
  name,
  disabled,
  error,
  errorMessage,
  multiple,
}: SelectHeaderProps) => {
  const [isOpen, setIsOpen] = useOpenState();
  const [selection] = useSelection();

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setIsOpen(false), "clickable");

  const handleClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const displayValue = useMemo(() => {
    if (multiple) {
      return selection.map((item) => item?.value || item).join(", ");
    }
    return selectValue?.[0]?.value || selection?.[0]?.value || "";
  }, [multiple, selection, selectValue]);

  const hasValue = Boolean(displayValue);

  return (
    <div aria-label="select-header" ref={ref} className={cn(headerClassName, styles.header)}>
      <button
        type="button"
        aria-label={name}
        aria-expanded={isOpen}
        disabled={disabled}
        className={cn(
          styles.headerField,
          { [styles.headerFieldOpen]: isOpen },
          { [styles.headerFieldFilled]: hasValue && !isOpen },
          { [styles.headerFieldError]: error },
          { [styles.headerFieldDisabled]: disabled }
        )}
        onClick={handleClick}
      >
        {hasValue && renderIcon && <span className={styles.headerIcon}>{renderIcon}</span>}
        <span
          className={cn(
            styles.headerText,
            { [styles.headerTextPlaceholder]: !hasValue },
            { [styles.headerTextEllipsis]: multiple && hasValue }
          )}
        >
          {hasValue ? displayValue : placeholder}
        </span>
        <ChevronIcon
          className={cn(styles.chevron, { [styles.chevronOpen]: isOpen }, { [styles.chevronDisabled]: disabled })}
        />
      </button>
      {errorMessage && (
        <span aria-label="error-message" className={styles.errorMessage}>
          {errorMessage}
        </span>
      )}
    </div>
  );
};
